import React from "react";
import { useTranslation } from "react-i18next";
import { ModalFooter, Spinner } from "reactstrap";
import { useSnapshot } from "valtio";

import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";

import { ConfirmSaveModal } from "./ConfirmSaveModal";
import { generalStore, resetAllPrinterSettingStores } from "./stores";
import { useSavePrinterSetting } from "./hooks";
import { PrinterRowData, PrinterSettingConfig } from "./types";
import { StyledLabel } from "@aureskonnect/react-ui";

const IMAGE_MIME_JPEG = "image/jpeg";
const IMAGE_MIME_PNG = "image/png";
const IMAGE_MIME_ICO = "image/ico";
const IMAGE_MIME_JPG = "image/jpg";
const BUTTON_HEIGHT_PX = "48px";
const DEFAULT_CONFIRM_TITLE_KEY = "Confirm save";
const DEFAULT_CONFIRM_MESSAGE_KEY =
    "Do you want to apply these printer settings?";

function isAcceptedImageMime(mime: string): boolean {
    return (
        mime === IMAGE_MIME_JPEG ||
        mime === IMAGE_MIME_PNG ||
        mime === IMAGE_MIME_ICO ||
        mime === IMAGE_MIME_JPG
    );
}

export type PrinterFooterProps = {
    config: PrinterSettingConfig;
    rowData: PrinterRowData;
    oneShop: boolean;
    onClose: () => void;
    onSaved?: () => void;
};

export function PrinterFooter({
    config,
    rowData,
    oneShop,
    onClose,
    onSaved,
}: PrinterFooterProps): JSX.Element {
    const { t } = useTranslation();
    const { save } = useSavePrinterSetting({ config, rowData, oneShop });
    const { typeLogoCustomer, typeLogoKitchen } = useSnapshot(generalStore);
    const [isConfirmOpen, setIsConfirmOpen] = React.useState<boolean>(false);
    const [isSaving, setIsSaving] = React.useState<boolean>(false);

    const customerLogoOk = isAcceptedImageMime(typeLogoCustomer);
    const kitchenLogoOk = isAcceptedImageMime(typeLogoKitchen);
    const customerLogoEmpty = typeLogoCustomer === "";
    const kitchenLogoEmpty = typeLogoKitchen === "";

    const canSave =
        (customerLogoOk && kitchenLogoOk) ||
        (customerLogoEmpty && kitchenLogoEmpty) ||
        (customerLogoOk && kitchenLogoEmpty) ||
        (kitchenLogoOk && customerLogoEmpty);

    const features = config.features ?? {};
    const confirmTitleKey =
        features.confirmTitleKey ?? DEFAULT_CONFIRM_TITLE_KEY;
    const confirmMessageKey =
        features.confirmMessageKey ?? DEFAULT_CONFIRM_MESSAGE_KEY;
    const confirmSecondaryMessageKey = features.confirmSecondaryMessageKey;

    const confirmMessage = (
        <StyledLabel>
            <span className="d-flex flex-column m-0">
                <span>{t(confirmMessageKey)}</span>
                {confirmSecondaryMessageKey ? (
                    <span>{t(confirmSecondaryMessageKey)}</span>
                ) : null}
            </span>
        </StyledLabel>
    );

    const runSave = React.useCallback(async () => {
        if (isSaving) return;
        setIsSaving(true);
        const ok = await save();
        if (!ok) {
            setIsSaving(false);
            return;
        }
        resetAllPrinterSettingStores();
        onSaved?.();
        onClose();
    }, [isSaving, save, onSaved, onClose]);

    const handleCancel = () => {
        resetAllPrinterSettingStores();
        onClose();
    };

    const handleValidate = () => {
        if (!canSave || isSaving) return;
        if (features.confirmBeforeSave) {
            setIsConfirmOpen(true);
            return;
        }
        runSave();
    };

    const handleConfirm = async () => {
        setIsConfirmOpen(false);
        await runSave();
    };

    const handleConfirmCancel = () => {
        setIsConfirmOpen(false);
    };

    return (
        <>
            <ModalFooter>
                <CustomSecondaryColorButton
                    outline
                    style={{ height: BUTTON_HEIGHT_PX }}
                    rounded
                    className="mr-2"
                    variant="light"
                    onClick={handleCancel}
                    disabled={isSaving}
                    data-testid="printer-setting-cancel-btn"
                >
                    <span>{t("Cancel")}</span>
                </CustomSecondaryColorButton>
                <CustomMainColorButton
                    rounded
                    style={{ height: BUTTON_HEIGHT_PX }}
                    variant="primary"
                    onClick={handleValidate}
                    disabled={isSaving}
                    aria-busy={isSaving}
                    data-testid="printer-setting-validate-btn"
                >
                    {isSaving ? (
                        <Spinner
                            size="sm"
                            className="mr-2"
                            aria-hidden="true"
                        />
                    ) : null}
                    {t("Validate")}
                </CustomMainColorButton>
            </ModalFooter>
            {features.confirmBeforeSave ? (
                <ConfirmSaveModal
                    isOpen={isConfirmOpen}
                    title={t(confirmTitleKey)}
                    message={confirmMessage}
                    confirmLabel={t("Validate")}
                    cancelLabel={t("Cancel")}
                    onConfirm={handleConfirm}
                    onCancel={handleConfirmCancel}
                />
            ) : null}
        </>
    );
}
